import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { attendanceService } from "../../api/attendanceService";
import Modal from "../../components/ui/Modal";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import {
  getDeviceLocation,
  getLocationPermission,
} from "../../utils/geolocation";
import { getEmployeeId } from "../../utils/session";
import { cx } from "../../utils/classNames";
import { MY_ATTENDANCE_SETTINGS_KEY, MY_TODAY_KEY } from "./sessionUtils";
import LocationHelp from "./components/LocationHelp";
import ui from "../../components/ui/ui.module.css";
import styles from "./Sessions.module.css";

// Asks for location access the first time an employee signs in
// (while the server says needsLocationConsent). The answer is saved, so it
// is asked once; the check-in screen keeps a "Location help" link.
const LocationConsentGate = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const employeeId = getEmployeeId();
  const [step, setStep] = useState("ask"); // "ask" | "locating" | "help"
  const [isClosed, setIsClosed] = useState(false);

  const settingsQuery = useQuery({
    queryKey: MY_ATTENDANCE_SETTINGS_KEY,
    queryFn: attendanceService.getMySettings,
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000,
  });

  const consentMutation = useMutation({
    mutationFn: attendanceService.saveLocationConsent,
    onSuccess: (settings) => {
      queryClient.setQueryData(MY_ATTENDANCE_SETTINGS_KEY, settings);
      queryClient.invalidateQueries({ queryKey: MY_TODAY_KEY });
    },
  });

  if (isClosed || !settingsQuery.data?.needsLocationConsent) return null;

  const close = () => setIsClosed(true);

  // Triggers the browser prompt, then saves the answer.
  const allow = async () => {
    setStep("locating");
    let granted;
    try {
      await getDeviceLocation("check in");
      granted = true;
    } catch {
      // A timeout or weak signal is not a refusal: trust the permission state.
      granted = (await getLocationPermission()) === "granted";
    }

    try {
      await consentMutation.mutateAsync(granted);
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          "Couldn't save your answer. We'll ask again next time.",
        ),
      );
    }

    if (granted) {
      toast.success("Location access allowed. You're ready to check in.");
      close();
    } else {
      setStep("help");
    }
  };

  const notNow = () => {
    consentMutation.mutate(false);
    close();
  };

  if (step === "help") {
    return (
      <Modal
        title="Location is blocked"
        onClose={close}
        footer={
          <button
            type="button"
            className={cx(ui.button, ui.primary)}
            onClick={close}
          >
            OK
          </button>
        }
      >
        <div className={cx(ui.alert, ui.alertWarning, "mb-3")}>
          Your browser didn't share your location, so you won't be able to check
          in yet. Allow it with the steps below, then reload the page.
        </div>
        <LocationHelp />
      </Modal>
    );
  }

  const isLocating = step === "locating";

  return (
    <Modal
      title="Allow location for check-in"
      onClose={notNow}
      footer={
        <>
          <button
            type="button"
            className={cx(ui.button, ui.secondary)}
            onClick={notNow}
            disabled={isLocating}
          >
            Not now
          </button>
          <button
            type="button"
            className={cx(ui.button, ui.primary)}
            onClick={allow}
            disabled={isLocating}
          >
            {isLocating ? "Waiting for your browser…" : "Allow"}
          </button>
        </>
      }
    >
      <p className="mb-2">
        <i className="bi bi-geo-alt-fill text-primary me-2"></i>
        Check-ins use your location to confirm you are at your branch.
      </p>
      <ul className={styles.consentPoints}>
        <li>
          Your location is only taken when you press Check in or Check out.
        </li>
        <li>
          It is used to validate the check-in, not to track you during the day.
        </li>
        <li>Check-in and check-out times always come from the server clock.</li>
      </ul>
      <p className={cx(ui.hint, "mb-0")}>
        After you press Allow, your browser asks for permission. Choose{" "}
        <strong>Allow</strong> there too.
      </p>
    </Modal>
  );
};

export default LocationConsentGate;
